import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { getMacawRoot } from '@/utils/macawRoot';
import './index.less';

// ─── 类型 ──────────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface ToastItem {
  id: number;
  type: ToastType;
  content: string;
  /** 毫秒；0 表示不自动关闭 */
  duration: number;
}

// ─── 图标 ──────────────────────────────────────────────────────────────────

const ICONS: Record<ToastType, React.ReactNode> = {
  success: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <circle cx="8" cy="8" r="7.5" stroke="#52c41a" />
      <path d="M4.5 8l2.5 2.5 4.5-5" stroke="#52c41a" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <circle cx="8" cy="8" r="7.5" stroke="#ff4d4f" />
      <path d="M5 5l6 6M11 5l-6 6" stroke="#ff4d4f" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <path d="M8 1.5L15 14H1L8 1.5z" stroke="#faad14" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 6v4M8 11.5v1" stroke="#faad14" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none">
      <circle cx="8" cy="8" r="7.5" stroke="#1677ff" />
      <path d="M8 7v5M8 4.5v1" stroke="#1677ff" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  loading: (
    <svg viewBox="0 0 16 16" width="16" height="16" className="toast-icon-spin" fill="none">
      <circle cx="8" cy="8" r="6" stroke="#1677ff" strokeWidth="2" strokeDasharray="28" strokeDashoffset="10" strokeLinecap="round" />
    </svg>
  ),
};

// ─── 单条 Toast ──────────────────────────────────────────────────────────────

const ToastItemView: React.FC<{ item: ToastItem; onClose: (id: number) => void }> = ({
  item,
  onClose,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // 进入动画
    const t1 = setTimeout(() => setVisible(true), 10);
    // 自动关闭
    if (item.duration > 0) {
      const t2 = setTimeout(() => {
        setVisible(false);
        setTimeout(() => onClose(item.id), 260);
      }, item.duration);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    return () => clearTimeout(t1);
  }, [item.duration, item.id, onClose]);

  return (
    <div className={`toast-item toast-item--${item.type}${visible ? ' toast-item--visible' : ''}`}>
      <span className="toast-item__icon">{ICONS[item.type]}</span>
      <span className="toast-item__content">{item.content}</span>
    </div>
  );
};

// ─── 容器组件 ────────────────────────────────────────────────────────────────

const ToastContainer: React.FC<{ items: ToastItem[]; onClose: (id: number) => void }> = ({
  items,
  onClose,
}) => (
  <div className="toast-container">
    {items.map((item) => (
      <ToastItemView key={item.id} item={item} onClose={onClose} />
    ))}
  </div>
);

// ─── 命令式驱动层 ─────────────────────────────────────────────────────────────

let _counter = 0;
let _items: ToastItem[] = [];
let _rerender: (() => void) | null = null;
let _handleClose: ((id: number) => void) | null = null;

/** 确保容器 DOM + React root 只创建一次 */
function ensureRoot() {
  if (_rerender) return;

  const host = getMacawRoot();
  const wrapper = document.createElement('div');
  wrapper.id = 'global-toast-root';
  host.appendChild(wrapper);

  const root = createRoot(wrapper);

  // 稳定的 handleClose 函数，避免每次 render 都改变
  _handleClose = (id: number) => {
    _items = _items.filter((i) => i.id !== id);
    _rerender?.();
  };

  const render = () => {
    const snapshot = [..._items];
    root.render(
      <ToastContainer
        items={snapshot}
        onClose={_handleClose!}
      />,
    );
  };

  _rerender = render;
  render();
}

function show(type: ToastType, content: string, duration = 3000) {
  ensureRoot();
  const id = ++_counter;
  _items = [..._items, { id, type, content, duration }];
  _rerender?.();
  return id;
}

// ─── 对外 API ─────────────────────────────────────────────────────────────────

/**
 * 全局命令式提示，可在任意位置（组件内外）调用。
 *
 * @example
 * import { toast } from '@/components/Message';
 * toast.success('保存成功');
 * toast.error('请求失败');
 * const id = toast.loading('生成中...', 0);  // duration=0 不自动关闭
 */
export const toast = {
  success: (content: string, duration?: number) => show('success', content, duration ?? 3000),
  error:   (content: string, duration?: number) => show('error',   content, duration ?? 4000),
  warning: (content: string, duration?: number) => show('warning', content, duration ?? 3000),
  info:    (content: string, duration?: number) => show('info',    content, duration ?? 3000),
  loading: (content: string, duration?: number) => show('loading', content, duration ?? 0),
  /** 手动关闭指定 id 的提示（配合 loading 使用） */
  close: (id: number) => {
    _items = _items.filter((i) => i.id !== id);
    _rerender?.();
  },
};

export default toast;
